import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { NavigationEnd, Router } from "@angular/router";
import { filter } from "rxjs/operators";
import { Subscription } from "rxjs";

import { MENU_SECTIONS } from "../sidebar/menu-items";
import { RouteInfo } from "../sidebar/sidebar.metadata";

interface Crumb {
  title: string;
  path?: string;
}

@Component({
  selector: "app-breadcrumb",
  templateUrl: "./breadcrumb.component.html",
  styleUrls: ["./breadcrumb.component.scss"],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  crumbs: Crumb[] = [];

  private routerSub?: Subscription;

  /**
   * Mantiene la ruta visible sincronizada con la navegación del layout autenticado.
   */
  constructor(
    private router: Router,
    private titleService: Title
  ) {}

  ngOnInit() {
    this.computeCrumbs(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => this.computeCrumbs(e.urlAfterRedirects));
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }

  navigate(crumb: Crumb) {
    if (crumb.path) {
      this.router.navigateByUrl(crumb.path);
    }
  }

  private computeCrumbs(url: string) {
    const cleanPath = (url || "").split("?")[0].split("#")[0];
    const trail = this.findTrail(cleanPath);

    if (trail.length > 0) {
      this.crumbs = trail;
      const last = trail[trail.length - 1];
      if (last?.title) this.titleService.setTitle(last.title);
      return;
    }

    const segments = cleanPath
      .replace(/^\/+/, "")
      .split("/")
      .filter(Boolean)
      .map((s) => this.humanize(s));

    this.crumbs = segments.map((title) => ({ title }));
    if (segments.length > 0) this.titleService.setTitle(segments[segments.length - 1]);
  }

  private findTrail(path: string): Crumb[] {
    for (const section of MENU_SECTIONS) {
      for (const item of section.items) {
        const found = this.searchInItem(item, path, []);
        if (found) return found;
      }
    }
    return [];
  }

  /**
   * Resuelve la jerarquía del menú y conserva el rastro en rutas parametrizadas.
   */
  private searchInItem(item: RouteInfo, path: string, trail: Crumb[]): Crumb[] | null {
    const here: Crumb = {
      title: item.title,
      path: item.path && item.path.trim() ? item.path : undefined,
    };
    const next = [...trail, here];

    if (item.path && (item.path === path || path.startsWith(`${item.path}/`))) return next;

    if (item.submenu?.length) {
      for (const child of item.submenu) {
        const found = this.searchInItem(child, path, next);
        if (found) return found;
      }
    }
    return null;
  }

  private humanize(seg: string): string {
    return seg
      .replace(/[-_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
